import {
  Box,
  Button,
  Checkbox,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import viewpaths from "global/constants/ViewPathConstants";
import { handleErrorMessage } from "helpers/methods";
import React, { useState } from "react";
import {
  insertRetargetingField,
  validateRetargetForm,
} from "screens/Campaigns/CampaignConstants";
import {
  getRetargetedGroupId,
  getFollowUpGroupId,
} from "screens/Campaigns/CampaignService";
import history from "utils/history";

interface CustomProps {
  open: boolean;
  setOpen: Function;
  id: any;
  successCount: number;
  openedCount: number;
  clickedCount: number;
  openedRate: any;
  clickedRate: any;
  notOpenedRate: any;
  isFollowUp: boolean;
  campaignName: string;
}
const RetargetModal = (props: CustomProps) => {
  const theme = useTheme();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [retargetingData, setRetargetingData] = useState(
    insertRetargetingField(props.campaignName)
  );
  const groupType = retargetingData?.notOpened.value ? "FollowUp" : "Retarget";
  const groupTypeNumber = retargetingData?.notOpened.value ? 5 : 3;
  const trimmedLength = retargetingData.groupName.value.trim().length;

  const OnFinish = async () => {
    try {
      if (handleValidation() && trimmedLength < 100) {
        setLoading(true);
        const updatedRetargetingData = {
          ...retargetingData,
          groupType: {
            value: groupType,
          },
        };
        const response = retargetingData?.notOpened.value
          ? await getFollowUpGroupId(props.id, retargetingData?.notOpened.value)
          : await getRetargetedGroupId(props.id, retargetingData?.open.value);
        setRetargetingData({ ...updatedRetargetingData, id: response.id });
        history.push(viewpaths.createRetargetCampaignViewPath, {
          parentId: props.id,
          groupId: response.message,
          name: props.campaignName,
          groupType: groupTypeNumber,
          opened: updatedRetargetingData?.open.value,
          status: groupType,
        });
        props.setOpen(false);
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      props.setOpen(true);
      handleErrorMessage(error);
    }
  };

  const handleValidation = () => {
    const { isValid, errors }: { isValid: boolean; errors: any } =
      validateRetargetForm(retargetingData);
    setRetargetingData({ ...errors });
    return isValid;
  };

  const emptyState = () => {
    setRetargetingData({
      groupName: {
        value: props.campaignName ?? "",
        error: "",
      },
      groupType: {
        value: groupType,
        error: "",
      },
      open: {
        value: false,
        error: "",
      },
      click: {
        value: false,
        error: "",
      },
      notOpened: {
        value: false,
        error: "",
      },
    });
  };

  const openCloseModalHandler = () => {
    props.setOpen(false);
    emptyState();
  };

  const updateGroupNameBasedOnSelection = (
    isOpenSelected: boolean,
    isClickSelected: boolean,
    isNotOpenedSelected: boolean
  ) => {
    let groupName = props?.campaignName || "";
    groupName = groupName.slice(0, 75);

    if (isOpenSelected && isClickSelected) {
      groupName += " (opened-click)";
    } else if (isClickSelected) {
      groupName += " (clicked)";
    } else if (isOpenSelected) {
      groupName += " (opened)";
    } else if (isNotOpenedSelected) {
      groupName += " (not-opened)";
    }

    setRetargetingData((prevData: any) => ({
      ...prevData,
      groupName: {
        value: groupName,
        error: "",
      },
    }));
  };

  const handleOpenedEmails = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRetargetingData({
      ...retargetingData,
      open: {
        ...retargetingData.open,
        value: isChecked,
        error: "",
      },
      notOpened: {
        ...retargetingData.notOpened,
        value: isChecked ? false : retargetingData.notOpened.value,
        error: "",
      },
    });
    updateGroupNameBasedOnSelection(
      isChecked,
      retargetingData.click.value,
      false
    );
  };

  const handleClickedEmails = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRetargetingData({
      ...retargetingData,
      click: {
        ...retargetingData.click,
        value: isChecked,
        error: "",
      },
      notOpened: {
        ...retargetingData.notOpened,
        value: isChecked ? false : retargetingData.notOpened.value,
        error: "",
      },
    });
    updateGroupNameBasedOnSelection(
      retargetingData.open.value,
      isChecked,
      false
    );
  };

  const handleNotOpenedEmails = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRetargetingData({
      ...retargetingData,
      notOpened: {
        ...retargetingData.notOpened,
        value: isChecked,
        error: "",
      },
      open: {
        ...retargetingData.open,
        value: isChecked ? false : retargetingData.open.value,
        error: "",
      },
      click: {
        ...retargetingData.click,
        value: isChecked ? false : retargetingData.click.value,
        error: "",
      },
    });
    updateGroupNameBasedOnSelection(false, false, isChecked);
  };

  const getBodyContent = () => {
    return (
      <>
        <Box>
          <Typography variant="body2" textAlign={"left"}>
            Engage with recipients who interacted with your campaign, such as
            those who clicked or opened.
          </Typography>
          <Grid container mt={2}>
            <Grid
              item
              xs={12}
              display={"flex"}
              flexDirection={"row"}
              gap={"20px"}
            >
              <Stack direction="row" alignItems="center">
                <Checkbox
                  onChange={handleOpenedEmails}
                  name="open"
                  checked={retargetingData.open.value}
                  disabled={props.openedCount <= 0}
                />
                <Typography
                  sx={{
                    color:
                      props.openedCount <= 0
                        ? "rgba(0, 0, 0, 0.26)"
                        : theme.palette.primary.dark,
                  }}
                  onChange={handleOpenedEmails}
                >
                  Opened{" "}
                  <span
                    style={{
                      fontSize: "12px",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {" "}
                    ({[props.openedRate]})
                  </span>
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  onChange={handleClickedEmails}
                  name="click"
                  checked={retargetingData.click.value}
                  disabled={props.clickedCount <= 0}
                />
                <Typography
                  sx={{
                    color:
                      props.clickedCount <= 0
                        ? "rgba(0, 0, 0, 0.26)"
                        : theme.palette.primary.dark,
                  }}
                  onChange={handleOpenedEmails}
                >
                  Clicked{" "}
                  <span
                    style={{
                      fontSize: "12px",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {" "}
                    ({[props.clickedRate]})
                  </span>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Typography variant="body2" textAlign={"left"} mb={2} mt={2}>
          Send a follow-up campaign to recipients who did not open your campaign
        </Typography>
        <Stack direction="row" alignItems="center">
          <Checkbox
            onChange={handleNotOpenedEmails}
            name="notOpened"
            checked={retargetingData.notOpened.value}
            disabled={!props.isFollowUp}
          />
          <Typography
            sx={{
              color: !props.isFollowUp
                ? "rgba(0, 0, 0, 0.26)"
                : theme.palette.primary.dark,
            }}
            onChange={handleOpenedEmails}
          >
            Not-Opened{" "}
            <span
              style={{
                fontSize: "12px",
                color: theme.palette.primary.main,
              }}
            >
              {" "}
              ({[props.notOpenedRate]})
            </span>
          </Typography>
        </Stack>
        <Box marginLeft={"15px"}>
          <FormHelperText error>
            {retargetingData.open.value === false
              ? retargetingData.open.error
              : ""}
          </FormHelperText>
        </Box>
        {getFooter()}
      </>
    );
  };

  const getFooter = () => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        mt={3}
        spacing={2}
        justifyContent="center"
      >
        <Button
          variant="outlined"
          id="retarget_cancel_button"
          onClick={openCloseModalHandler}
        >
          Cancel
        </Button>
        <Button
          id="retarget_submit_button"
          variant="contained"
          onClick={OnFinish}
          disabled={isLoading}
        >
          Submit
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={() => props.setOpen(false)}
        isDialogOpen={props.open}
        body={getBodyContent()}
        title={
          <Typography variant="h4" color={theme.palette.primary.main}>
            Retarget Audience
          </Typography>
        }
        closable
        closeButtonVisibility
        maxWidth="sm"
      />
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default RetargetModal;
